import { useQuery } from '@apollo/client';
import { IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import clsx from 'clsx';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback, useMemo } from 'react';
import {
  PaymentAvailableDocumentTypes,
  PaymentAvailableDocumentTypesVariables,
} from '../../__generated__/PaymentAvailableDocumentTypes';
import {
  AttachablePaymentDocuments,
  AttachablePaymentDocumentsVariables,
  AttachablePaymentDocuments_attachablePaymentDocuments_nodes,
} from './__generated__/AttachablePaymentDocuments';
import { _columns } from './columns.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import ATTACHABLE_PAYEMENT_DOCUMENTS from './AttachablePaymentDocuments.graphql';
import PAYMENT_AVAILABLE_DOCUMENT_TYPES from '../../PaymentAvailableDocumentTypes.graphql';

export type AttachableDataType =
  AttachablePaymentDocuments_attachablePaymentDocuments_nodes;

export interface AttachedDocumentsDataType {
  documentTypeId: number | null | undefined;
  entityDocumentId: string[] | null;
  attachedDocuments: AttachableDataType[];
}

interface PaymentDocumentsProps {
  transactionTypeId: number;
  payCycleId: string;
  paymentId?: string;
  isOpen?: boolean;
  dismissModal?: (param: boolean) => void;
  onDocumentsAttached: (
    attachedDocumentsData: AttachedDocumentsDataType
  ) => void;
  prevSelectedDocuments?: AttachableDataType[];
}

export const PaymentDocuments: React.FC<PaymentDocumentsProps> = ({
  transactionTypeId,
  payCycleId,
  paymentId,
  onDocumentsAttached,
  prevSelectedDocuments,
}) => {
  const styles = useStyles();
  const { data: paymentAvailableDocumentTypesData } = useQuery<
    PaymentAvailableDocumentTypes,
    PaymentAvailableDocumentTypesVariables
  >(PAYMENT_AVAILABLE_DOCUMENT_TYPES, {
    variables: {
      transactionTypeId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const getDocumentTypeOptions = useCallback(
    (data: PaymentAvailableDocumentTypes | undefined) => {
      return (
        data?.paymentAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
        })) || []
      );
    },
    []
  );

  const transformData = useMemo(
    () => (data: AttachablePaymentDocuments | undefined) => {
      const documents = data?.attachablePaymentDocuments?.nodes.map(
        (document) =>
          ({
            ...document,
            available: document.documentAppliedAmounts?.remainingTotal,
            isoCode: document.currency?.isoCode,
          } as AttachableDataType)
      );
      if (prevSelectedDocuments?.length && documents?.length) {
        const filteredArray = documents.filter(
          ({ id: id1 }) =>
            !prevSelectedDocuments.some(({ id: id2 }) => id2 === id1)
        );
        return filteredArray;
      }
      return documents;
    },
    [prevSelectedDocuments]
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.documentTypes?.documentType}</Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.currency?.isoCode}</Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{getGlobalDateFormat(item._uploadDate!)}</Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <AmountTextView
              className={clsx(styles.contentColumnAlignRight)}
              value={item.indexAmount}
            />
          );
        case 'action':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  const getMutationInput = useCallback(
    (
      selectedItems: AttachableDataType[],
      _entityId: string,
      documentTypeId?: number | null
    ) => {
      const documentsIDs: string[] = selectedItems.map((ele) => ele.id);
      return {
        documentTypeId,
        entityDocumentId: documentsIDs,
        attachedDocuments: selectedItems,
      } as AttachedDocumentsDataType;
    },
    []
  );

  const onAttachedSuccessfully = useCallback(
    (selectedItems?: AttachableDataType[]) => {
      if (!selectedItems || selectedItems.length === 0) return;

      const documentsIDs: string[] = selectedItems.map((ele) => ele.id);
      onDocumentsAttached({
        documentTypeId: undefined, // documentTypeId is managed internally by GenericAttachForm
        entityDocumentId: documentsIDs,
        attachedDocuments: selectedItems,
      });
    },
    [onDocumentsAttached]
  );

  return (
    <Stack horizontal horizontalAlign="end">
      <GenericAttachForm<
        AttachableDataType,
        AttachablePaymentDocuments,
        AttachablePaymentDocumentsVariables,
        unknown,
        AttachedDocumentsDataType,
        PaymentAvailableDocumentTypes
      >
        entity={{ id: paymentId }}
        availableDocumentTypes={paymentAvailableDocumentTypesData}
        attachableDocumentsQuery={ATTACHABLE_PAYEMENT_DOCUMENTS}
        detailsQuery={ATTACHABLE_PAYEMENT_DOCUMENTS} // Dummy, as not used
        columns={_columns}
        modalWidth={1300}
        getDocumentTypeOptions={getDocumentTypeOptions}
        transformData={transformData}
        getMutationInput={getMutationInput}
        onRenderItemColumn={onRenderItemColumn}
        initialQueryVariables={{
          payCycleId,
          paymentId,
          first: TABLE_ROWS,
        }}
        toOrderByVariable={toOrderByVariable}
        onAttachedSuccessfully={onAttachedSuccessfully}
        buttonRightAligned
      />
    </Stack>
  );
};
